import Mustache from "mustache"

export type ArrayRow = Record<string, unknown>

export interface ArrayVariable {
	name: string
	properties: string[]
	rows: ArrayRow[]
	booleanProperties: string[]
}

export interface ExtractedMustacheVariables {
	variables: string[]
	arrays: ArrayVariable[]
	conditionals: string[]
}

export const MUSTACHE_META_KEY = "__meta"

export interface MustacheVariablesMeta {
	arrayDisplayMode?: Record<string, string>
	listItemFields?: Record<string, string[]>
}

export interface ExtractMustacheVariablesOptions {
	existingVariables?: Record<string, any>
	getSampleValue?: (name: string) => string
}

const META_KEYS = new Set([MUSTACHE_META_KEY])

function uniqueSorted(values: Iterable<string>): string[] {
	return Array.from(new Set(values)).sort()
}

function ensureSet(map: Record<string, Set<string>>, key: string): Set<string> {
	if (!map[key]) {
		map[key] = new Set()
	}
	return map[key]
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}

export function getMustacheMeta(variables: Record<string, any> | undefined): MustacheVariablesMeta {
	const meta = variables?.[MUSTACHE_META_KEY]
	return isPlainObject(meta) ? (meta as MustacheVariablesMeta) : {}
}

export function withMustacheMeta(variables: Record<string, any>, meta: MustacheVariablesMeta): Record<string, any> {
	const next = {...variables}
	const hasArrayDisplayMode = meta.arrayDisplayMode && Object.keys(meta.arrayDisplayMode).length > 0
	const hasListItemFields = meta.listItemFields && Object.keys(meta.listItemFields).length > 0

	if (!hasArrayDisplayMode && !hasListItemFields) {
		delete next[MUSTACHE_META_KEY]
		return next
	}

	next[MUSTACHE_META_KEY] = {
		...(hasArrayDisplayMode ? {arrayDisplayMode: meta.arrayDisplayMode} : {}),
		...(hasListItemFields ? {listItemFields: meta.listItemFields} : {}),
	}
	return next
}

function coerceBoolean(value: unknown): boolean {
	if (typeof value === "boolean") return value
	if (typeof value === "number") return value !== 0
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase()
		if (normalized === "" || normalized === "false" || normalized === "0") return false
		return true
	}
	return Boolean(value)
}

function allValuesAreBoolean(rows: ArrayRow[], key: string): boolean {
	const values = rows.map((row) => row[key]).filter((value) => value !== undefined)
	return values.length > 0 && values.every((value) => typeof value === "boolean")
}

function collectItemKeys(rows: unknown): string[] {
	if (!Array.isArray(rows)) return []
	const keys = new Set<string>()
	rows.forEach((row) => {
		if (!isPlainObject(row)) return
		Object.keys(row).forEach((key) => {
			if (!META_KEYS.has(key)) keys.add(key)
		})
	})
	return Array.from(keys)
}

function walkMustacheTokens(
	tokens: any[],
	parentSection: string | undefined,
	depth: number,
	state: {
		allVariables: string[]
		allConditionals: Set<string>
		positiveCondVars: Set<string>
		negativeCondVars: Set<string>
		definiteConditionals: Set<string>
		interpolationsBySection: Record<string, Set<string>>
		nestedSectionsByParent: Record<string, Set<string>>
	}
) {
	tokens.forEach((token) => {
		const [type, name] = token
		if (type === "name" || type === "&") {
			state.allVariables.push(name)
			if (parentSection && name !== parentSection) {
				ensureSet(state.interpolationsBySection, parentSection).add(name)
			}
			return
		}

		if (type !== "#" && type !== "^") return

		state.allConditionals.add(name)
		if (type === "#") {
			state.positiveCondVars.add(name)
		} else {
			state.negativeCondVars.add(name)
		}
		if (state.positiveCondVars.has(name) && state.negativeCondVars.has(name)) {
			state.definiteConditionals.add(name)
		}

		ensureSet(state.interpolationsBySection, name)
		ensureSet(state.nestedSectionsByParent, name)
		if (parentSection && name !== parentSection) {
			ensureSet(state.nestedSectionsByParent, parentSection).add(name)
		}

		const children = Array.isArray(token[4]) ? token[4] : []
		if (depth < 10 && children.length > 0) {
			walkMustacheTokens(children, name, depth + 1, state)
		}
	})
}

function buildSampleRows(
	properties: string[],
	booleanProperties: Set<string>,
	getSampleValue: (name: string) => string
): ArrayRow[] {
	return [0, 1].map((index) =>
		properties.reduce((row, prop) => {
			if (booleanProperties.has(prop)) {
				row[prop] = index === 0
			} else {
				row[prop] = index === 0 ? getSampleValue(prop) : `${getSampleValue(prop)} (2)`
			}
			return row
		}, {} as ArrayRow)
	)
}

export function extractMustacheVariables(
	html: string,
	options: ExtractMustacheVariablesOptions = {}
): ExtractedMustacheVariables {
	const existingVariables = options.existingVariables ?? {}
	const getSampleValue = options.getSampleValue ?? ((name: string) => `Sample ${name}`)

	const allVariables: string[] = []
	const interpolationsBySection: Record<string, Set<string>> = {}
	const nestedSectionsByParent: Record<string, Set<string>> = {}
	const definiteConditionals: Set<string> = new Set()
	const positiveCondVars: Set<string> = new Set()
	const negativeCondVars: Set<string> = new Set()
	const allConditionals: Set<string> = new Set()

	const conditionalsPattern = /{{[#^]([a-zA-Z0-9_]+)}}/g
	let match
	while ((match = conditionalsPattern.exec(html)) !== null) {
		const [fullMatch, varName] = match
		if (fullMatch.includes("{{#")) {
			positiveCondVars.add(varName)
		}
		if (fullMatch.includes("{{^")) {
			negativeCondVars.add(varName)
		}
		allConditionals.add(varName)
		if (positiveCondVars.has(varName) && negativeCondVars.has(varName)) {
			definiteConditionals.add(varName)
		}
	}

	const selfRefPattern = /{{[#^]([a-zA-Z0-9_]+)}}[.\s\S]*?{{([a-zA-Z0-9_]+)}}[.\s\S]*?{{\/\1}}/g
	while ((match = selfRefPattern.exec(html)) !== null) {
		const [, sectionName, innerVariable] = match
		if (sectionName === innerVariable) {
			definiteConditionals.add(sectionName)
		}
	}

	const isUsedInBothContexts = (name: string): boolean => positiveCondVars.has(name) && negativeCondVars.has(name)
	const isComplexConditional = (name: string): boolean => html.includes(`{{#${name}}}`) && html.includes(`{{^${name}}}`)
	const isSelfReferential = (name: string): boolean => html.includes(`{{#${name}}}`) && html.includes(`{{${name}}}`)

	walkMustacheTokens(Mustache.parse(html), undefined, 0, {
		allVariables,
		allConditionals,
		positiveCondVars,
		negativeCondVars,
		definiteConditionals,
		interpolationsBySection,
		nestedSectionsByParent,
	})

	const sections = interpolationsBySection

	const conditionalVars = Array.from(allConditionals).filter((name) => {
		if (definiteConditionals.has(name)) {
			return true
		}

		const hasSubstantialProperties = sections[name] && sections[name].size > 2
		if (hasSubstantialProperties) {
			return false
		}

		if (isUsedInBothContexts(name)) {
			return true
		}

		if (isComplexConditional(name) || isSelfReferential(name)) {
			return true
		}

		return !sections[name] || sections[name].size === 0 || (sections[name].size <= 2 && allVariables.includes(name))
	})

	const arrayNames = Object.entries(sections)
		.filter(([name, props]) => {
			if (conditionalVars.includes(name)) {
				return false
			}
			if (props.size > 2) {
				return true
			}
			if (
				definiteConditionals.has(name) ||
				isUsedInBothContexts(name) ||
				isComplexConditional(name) ||
				isSelfReferential(name)
			) {
				return false
			}
			const isArray = props.size > 0
			const isUsedAsVariable = allVariables.includes(name)
			const isSimpleConditional = props.size <= 2 && isUsedAsVariable
			return isArray && !isSimpleConditional
		})
		.map(([name]) => name)

	const arrayNameSet = new Set(arrayNames)
	const meta = getMustacheMeta(existingVariables)
	const listItemFields = meta.listItemFields ?? {}

	const arrays: ArrayVariable[] = arrayNames.map((name) => {
		const interpolationProperties = Array.from(sections[name] ?? [])
		const nestedBooleanProperties = Array.from(nestedSectionsByParent[name] ?? []).filter((child) =>
			conditionalVars.includes(child)
		)
		const existingArray = existingVariables[name]
		const existingRows = Array.isArray(existingArray) ? (existingArray as ArrayRow[]) : []
		const extraItemKeys = collectItemKeys(existingRows).filter((key) => !arrayNameSet.has(key))
		const assignedFields = (listItemFields[name] ?? []).filter((key) => !arrayNameSet.has(key))

		const booleanProperties = uniqueSorted([
			...nestedBooleanProperties,
			...extraItemKeys.filter((key) => allValuesAreBoolean(existingRows, key) || allConditionals.has(key)),
			...assignedFields.filter((key) => allConditionals.has(key) || nestedBooleanProperties.includes(key)),
		])
		const properties = uniqueSorted([
			...interpolationProperties,
			...nestedBooleanProperties,
			...extraItemKeys,
			...assignedFields,
		])

		let rows: ArrayRow[] = [{}]
		if (existingRows.length > 0) {
			rows = existingRows
		} else {
			rows = buildSampleRows(properties, new Set(booleanProperties), getSampleValue)
		}

		return {name, properties, rows, booleanProperties}
	})

	const nestedListConditionals = new Set(arrays.flatMap((arrayVar) => arrayVar.booleanProperties))
	const arrayProperties = new Set(arrays.flatMap((arrayVar) => arrayVar.properties))

	const conditionals = conditionalVars.filter((name) => !nestedListConditionals.has(name) && !arrayProperties.has(name))

	const variables = allVariables
		.filter((value, index, self) => self.indexOf(value) === index)
		.filter((v) => !conditionals.includes(v))
		.filter((v) => !arrayProperties.has(v))
		.sort()

	return {variables, arrays, conditionals}
}

export function omitRootCopiesOfListItemFields(
	variables: Record<string, any>,
	arrays: ArrayVariable[]
): Record<string, any> {
	const next = {...variables}
	const promoted = new Set(arrays.flatMap((arrayVar) => arrayVar.booleanProperties))

	promoted.forEach((key) => {
		if (!(key in next) || Array.isArray(next[key]) || isPlainObject(next[key])) {
			return
		}

		const topLevelValue = next[key]
		arrays.forEach((arrayVar) => {
			if (!arrayVar.booleanProperties.includes(key)) return
			const currentRows = Array.isArray(next[arrayVar.name]) ? (next[arrayVar.name] as ArrayRow[]) : arrayVar.rows
			next[arrayVar.name] = currentRows.map((row) => {
				if (!isPlainObject(row) || key in row) return row
				return {...row, [key]: coerceBoolean(topLevelValue)}
			})
		})
		delete next[key]
	})

	return next
}

export function moveFieldIntoList(
	variables: Record<string, any>,
	arrays: ArrayVariable[],
	fieldName: string,
	targetListName: string,
	asBoolean = true
): {variables: Record<string, any>; arrays: ArrayVariable[]} {
	const target = arrays.find((arrayVar) => arrayVar.name === targetListName)
	if (!target) {
		return {variables, arrays}
	}

	const topLevelValue = variables[fieldName]
	const nextArrays = arrays.map((arrayVar) => {
		if (arrayVar.name !== targetListName) return arrayVar

		const properties = uniqueSorted([...arrayVar.properties, fieldName])
		const booleanProperties = asBoolean
			? uniqueSorted([...arrayVar.booleanProperties, fieldName])
			: arrayVar.booleanProperties.filter((name) => name !== fieldName)
		const sourceRows = Array.isArray(variables[arrayVar.name])
			? (variables[arrayVar.name] as ArrayRow[])
			: arrayVar.rows
		const rows = sourceRows.map((row) => {
			const nextRow = isPlainObject(row) ? {...row} : {}
			if (!(fieldName in nextRow)) {
				nextRow[fieldName] = asBoolean ? coerceBoolean(topLevelValue) : topLevelValue ?? ""
			}
			return nextRow
		})

		return {...arrayVar, properties, booleanProperties, rows}
	})

	const meta = getMustacheMeta(variables)
	const listItemFields = {...(meta.listItemFields ?? {})}
	listItemFields[targetListName] = uniqueSorted([...(listItemFields[targetListName] ?? []), fieldName])

	const nextVariables: Record<string, any> = {...variables}
	delete nextVariables[fieldName]
	const updatedTarget = nextArrays.find((arrayVar) => arrayVar.name === targetListName)
	if (updatedTarget) {
		nextVariables[targetListName] = updatedTarget.rows
	}

	return {
		variables: withMustacheMeta(nextVariables, {...meta, listItemFields}),
		arrays: nextArrays,
	}
}

export function getFieldsAddableToList(
	arrays: ArrayVariable[],
	conditionals: string[],
	targetListName: string
): string[] {
	const target = arrays.find((arrayVar) => arrayVar.name === targetListName)
	if (!target) return []

	const alreadyOnTarget = new Set(target.properties)
	const fromConditionals = conditionals.filter((name) => !alreadyOnTarget.has(name))
	const fromOtherLists = arrays
		.filter((arrayVar) => arrayVar.name !== targetListName)
		.flatMap((arrayVar) => arrayVar.booleanProperties)
		.filter((name) => !alreadyOnTarget.has(name))

	return uniqueSorted([...fromConditionals, ...fromOtherLists])
}
